/**
 * Backlog view component.
 * @module components/View/BacklogView
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import Cookies from 'universal-cookie';

import { Icon } from '@plone/volto/components';
import { getContent, getVocabulary } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import clearSVG from '@plone/volto/icons/clear.svg';

const BACKLOG_SUBREQUEST = 'backlog';

const BACKLOG_TAB_COOKIE = 'backlog-tab';

// Reads the last-selected tab from its cookie (defaulting to "orders" when
// there's no stored value yet), tolerating cookies being disabled/missing.
const readTab = () => {
  try {
    const stored = new Cookies().get(BACKLOG_TAB_COOKIE);
    return stored === 'tools' ? 'tools' : 'orders';
  } catch (error) {
    return 'orders';
  }
};

// EUR has no live exchange rate of its own yet, so each tool is shown as
// a flat $5 markup over its own USD price. An amount covering several
// tools (an order's total) therefore needs $5 added per tool it covers,
// not just once over the whole total.
const formatAmount = (amount, currency, units = 1) => {
  const value = Number(amount) || 0;
  const converted = currency === 'EUR' ? value + 5 * units : value;
  const symbol = currency === 'EUR' ? '€' : '$';
  return `${symbol} ${converted.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const resolveLabel = (vocabulary, value) =>
  vocabulary.find((item) => item.value === value)?.label || value || '—';

/**
 * Backlog view component class.
 * @function BacklogView
 * @returns {string} Markup of the component.
 */
const BacklogView = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currency = useSelector((state) => state.currency);
  const [tab, setTab] = useState(readTab);

  useEffect(() => {
    try {
      new Cookies().set(BACKLOG_TAB_COOKIE, tab, {
        path: '/',
        expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      });
    } catch (error) {
      // Ignore (e.g. cookies disabled).
    }
  }, [tab]);

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
    dispatch(getContent('/@backlog', null, BACKLOG_SUBREQUEST));
  }, [dispatch]);

  const contactsVocabulary = useSelector(
    (state) => state.vocabularies?.contacts?.items || [],
  );

  const items = useSelector(
    (state) => state.content.subrequests?.[BACKLOG_SUBREQUEST]?.data?.items,
  );
  const bySize = useSelector(
    (state) => state.content.subrequests?.[BACKLOG_SUBREQUEST]?.data?.bySize,
  );

  // Filtering
  const [orderId, setOrderId] = useState('');
  const [contact, setContact] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');

  const orderIdRef = useRef(null);
  const contactRef = useRef(null);

  const filterField = (list, field, value) =>
    value === ''
      ? list
      : list.filter(
          (item) =>
            item[field] !== undefined &&
            item[field] !== null &&
            String(item[field]).toLowerCase().indexOf(value.toLowerCase()) !==
              -1,
        );

  const filterDateRange = (list, from, till) => {
    let filtered = list;
    if (from) {
      const fromTime = new Date(from).getTime();
      filtered = filtered.filter(
        (item) =>
          item.order_date && new Date(item.order_date).getTime() >= fromTime,
      );
    }
    if (till) {
      // "Till" is a date with no time, so make it inclusive of the whole
      // day rather than cutting off at its midnight.
      const tillTime = new Date(till).getTime() + 24 * 60 * 60 * 1000 - 1;
      filtered = filtered.filter(
        (item) =>
          item.order_date && new Date(item.order_date).getTime() <= tillTime,
      );
    }
    return filtered;
  };

  let filteredItems = (items || []).map((item) => ({
    ...item,
    contactLabel: resolveLabel(contactsVocabulary, item.contact),
  }));
  filteredItems = filterField(filteredItems, 'title', orderId);
  filteredItems = filterField(filteredItems, 'contactLabel', contact);
  filteredItems = filterDateRange(filteredItems, dateFrom, dateTill);

  const total = filteredItems.reduce(
    (sum, item) => sum + (Number(item.total) || 0),
    0,
  );
  const totalLines = filteredItems.reduce(
    (sum, item) => sum + (Number(item.lines) || 0),
    0,
  );

  const toolsQuantity = (bySize || []).reduce(
    (sum, entry) => sum + (Number(entry.quantity) || 0),
    0,
  );

  return (
    <article id="backlog">
      <div className="button-toolbar">
        <div className="tab-toggle">
          <Button
            className={tab === 'orders' ? 'primary' : ''}
            type="button"
            onClick={() => setTab('orders')}
          >
            Orders
          </Button>
          <Button
            className={tab === 'tools' ? 'primary' : ''}
            type="button"
            onClick={() => setTab('tools')}
          >
            Tools
          </Button>
        </div>
        <div className="button-toolbar-title">
          Backlog{' '}
          <Label circular>
            {tab === 'tools' ? bySize?.length || 0 : items?.length || 0}
          </Label>
        </div>
      </div>
      <div className="view-padding">
        {tab === 'orders' && (
          <div className="scrollable-table">
            <Table striped celled selectable>
              <TableHeader>
                <TableRow className="tools-header">
                  <TableHeaderCell width="2">Order #</TableHeaderCell>
                  <TableHeaderCell width="4">Contact</TableHeaderCell>
                  <TableHeaderCell width="2">Date</TableHeaderCell>
                  <TableHeaderCell width="2">Inventory</TableHeaderCell>
                  <TableHeaderCell width="2">Total</TableHeaderCell>
                </TableRow>
                <TableRow className="tools-filter">
                  <TableHeaderCell>
                    <div className="filter-input">
                      <Input
                        value={orderId}
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        placeholder="Filter order #"
                        ref={orderIdRef}
                        onChange={(e) => setOrderId(e.target.value)}
                      />
                      {orderId && (
                        <Icon
                          name={clearSVG}
                          size="16px"
                          className="clear-icon"
                          onClick={() => {
                            setOrderId('');
                            orderIdRef.current?.focus();
                          }}
                        />
                      )}
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <div className="filter-input">
                      <Input
                        value={contact}
                        placeholder="Filter contact"
                        autoCorrect="off"
                        spellCheck="false"
                        ref={contactRef}
                        onChange={(e) => setContact(e.target.value)}
                      />
                      {contact && (
                        <Icon
                          name={clearSVG}
                          size="16px"
                          className="clear-icon"
                          onClick={() => {
                            setContact('');
                            contactRef.current?.focus();
                          }}
                        />
                      )}
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <div className="filter-date-range">
                      <input
                        type="date"
                        aria-label="From"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                      <input
                        type="date"
                        aria-label="Till"
                        value={dateTill}
                        onChange={(e) => setDateTill(e.target.value)}
                      />
                      {(dateFrom || dateTill) && (
                        <Icon
                          name={clearSVG}
                          size="16px"
                          className="clear-icon"
                          onClick={() => {
                            setDateFrom('');
                            setDateTill('');
                          }}
                        />
                      )}
                    </div>
                  </TableHeaderCell>
                  <TableHeaderCell />
                  <TableHeaderCell />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow
                    key={item['@id']}
                    className="clickable-row"
                    onClick={() => history.push(flattenToAppURL(item['@id']))}
                  >
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{item.contactLabel}</TableCell>
                    <TableCell>{formatDate(item.order_date)}</TableCell>
                    <TableCell>
                      {item.fulfillable} of {item.lines}
                    </TableCell>
                    <TableCell>
                      {formatAmount(item.total, currency, item.lines)}
                    </TableCell>
                  </TableRow>
                ))}
                {!filteredItems.length && (
                  <TableRow>
                    <TableCell colspan="5">
                      No orders awaiting payment
                    </TableCell>
                  </TableRow>
                )}
                {!!filteredItems.length && (
                  <TableRow className="grand-total-row">
                    <TableCell colspan="4" />
                    <TableCell>
                      <strong>
                        {formatAmount(total, currency, totalLines)}
                      </strong>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {tab === 'tools' && (
          <div className="scrollable-table">
            <Table striped celled selectable>
              <TableHeader>
                <TableRow className="tools-header">
                  <TableHeaderCell width="6">Tool</TableHeaderCell>
                  <TableHeaderCell width="2">Price</TableHeaderCell>
                  <TableHeaderCell width="2">Inventory</TableHeaderCell>
                  <TableHeaderCell width="2">Amount</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(bySize || []).map((entry) => (
                  <TableRow
                    key={entry.code}
                    className={entry['@id'] ? 'clickable-row' : ''}
                    onClick={() =>
                      entry['@id'] &&
                      history.push(flattenToAppURL(entry['@id']))
                    }
                  >
                    <TableCell>{entry.label}</TableCell>
                    <TableCell>{formatAmount(entry.price, currency)}</TableCell>
                    <TableCell>{entry.inventory}</TableCell>
                    <TableCell>{entry.quantity}</TableCell>
                  </TableRow>
                ))}
                {!bySize?.length && (
                  <TableRow>
                    <TableCell colspan="4">No tools needed</TableCell>
                  </TableRow>
                )}
                {!!bySize?.length && (
                  <TableRow className="grand-total-row">
                    <TableCell colspan="3" />
                    <TableCell>
                      <strong>{toolsQuantity}</strong>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </article>
  );
};

export default BacklogView;
